import type { z } from "zod";
import request, {
  RequestError,
  requestResponse,
  type JsonValueInput,
  type RequestContext,
  type RequestOutput,
  type RequestArgs,
  type RequestEndpoint,
  type StringCastable,
} from "./request.ts";
import type { RouterIndex } from "../router/index.ts";
import {
  streamEndpoints,
  type StreamEvent,
  type StreamInput,
  type StreamPath,
} from "./stream-endpoints.ts";

export type Api = RouterIndex;
export const SERVICE_ACCOUNT_HEADER = "X-Service-Account";
export const SERVICE_ACCOUNT_HEADER_VALUE = "true";
export const BASE_PATH = "/api/v2";
// The client that makes a write declares itself, so an article revision names
// its channel. The API derives `system` from a service account and `api` from
// no header; the value never changes who the author is.
export const CHANNEL_HEADER = "X-Juisci-Channel";
export const CHANNEL_HEADER_VALUES = ["admin", "mcp"] as const;

type Get = Api["get"];
type Post = Api["post"];
type Patch = Api["patch"];
type Put = Api["put"];
type Del = Api["delete"];

type GetP = keyof Get;
type PostP = keyof Post;
type PatchP = keyof Patch;
type PutP = keyof Put;
type DelP = keyof Del;

type Out<E extends RequestEndpoint> = Promise<RequestOutput<E>>;
type In<E extends RequestEndpoint> = RequestArgs<E>;

// convenience functions
export function createApiClient(ct: RequestContext): ApiClient {
  // This factory is api-specific; tag dev-console logs accordingly so the
  // generic request.ts library stays backend-agnostic.
  const context: RequestContext = { ...ct, logPrefix: "api" };
  return {
    get: (path, ...args) => request(context, BASE_PATH, "get", path, ...args),
    post: (path, ...args) => request(context, BASE_PATH, "post", path, ...args),
    patch: (path, ...args) =>
      request(context, BASE_PATH, "patch", path, ...args),
    put: (path, ...args) => request(context, BASE_PATH, "put", path, ...args),
    delete: (path, ...args) =>
      request(context, BASE_PATH, "delete", path, ...args),
    request: (method, path, ...args) =>
      request(context, BASE_PATH, method, path, ...args),
  };
}

export interface ApiClient {
  get: <P extends GetP>(path: P, ...args: In<Get[P]>) => Out<Get[P]>;
  post: <P extends PostP>(path: P, ...args: In<Post[P]>) => Out<Post[P]>;
  patch: <P extends PatchP>(path: P, ...args: In<Patch[P]>) => Out<Patch[P]>;
  put: <P extends PutP>(path: P, ...args: In<Put[P]>) => Out<Put[P]>;
  delete: <P extends DelP>(path: P, ...args: In<Del[P]>) => Out<Del[P]>;
  request: <E extends RequestEndpoint>(
    method: E["method"],
    path: E["path"],
    ...args: In<E>
  ) => Out<E>;
}

// ── Streaming client ────────────────────────────────────────────────────
// Typed SSE stream client. Path, input, and event shape are pinned to the
// registry in ./stream-endpoints.ts. Reuses RequestContext (auth, fetch,
// onRequestError) so errors flow through the same ClientError/ServerError
// path as regular apiClient requests — getRequestErrorMessage and the
// auth handler work uniformly.

export interface ApiStreamReader<P extends StreamPath> {
  read: () => Promise<
    { done: false; value: StreamEvent<P> } | { done: true; value: undefined }
  >;
  cancel: () => Promise<void>;
}

export interface ApiStreamClient {
  post: <P extends StreamPath>(
    path: P,
    args: StreamInput<P> & { signal?: AbortSignal },
  ) => Promise<ApiStreamReader<P>>;
}

interface StreamArgsImpl {
  param?: Record<string, StringCastable>;
  json: JsonValueInput;
  signal?: AbortSignal;
}

export function createApiStreamClient(ctx: RequestContext): ApiStreamClient {
  return {
    post: <P extends StreamPath>(
      path: P,
      args: StreamInput<P> & { signal?: AbortSignal },
    ) => openStream<P>(ctx, path, args),
  };
}

async function openStream<P extends StreamPath>(
  ctx: RequestContext,
  path: P,
  args: StreamArgsImpl,
): Promise<ApiStreamReader<P>> {
  const { response } = await requestResponse(ctx, BASE_PATH, "post", path, {
    param: args.param,
    json: args.json,
    header: { "Cache-Control": "no-cache" },
    signal: args.signal,
  });

  if (response.body === null) {
    const reqError = new RequestError("No response body for stream", {
      method: "post",
      path,
    });
    ctx.onRequestError?.(reqError);
    throw reqError;
  }

  const eventSchema: (typeof streamEndpoints)[P]["event"] =
    streamEndpoints[path].event;
  return createStreamReader(response.body, eventSchema);
}

function createStreamReader<S extends z.ZodType>(
  body: ReadableStream<Uint8Array>,
  eventSchema: S,
) {
  const reader = body.getReader();
  const decoder = new TextDecoder("utf-8");
  let buffer = "";
  const queue: z.output<S>[] = [];
  let upstreamDone = false;

  return {
    read: async () => {
      while (queue.length === 0 && !upstreamDone) {
        const { done, value } = await reader.read();
        if (done) {
          upstreamDone = true;
          const tail = buffer.trim();
          if (tail !== "") {
            const parsed = parseEvent(tail, eventSchema);
            if (parsed !== null) queue.push(parsed);
            buffer = "";
          }
          break;
        }
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop() ?? "";
        for (const part of parts) {
          const trimmed = part.trim();
          if (trimmed === "") continue;
          const parsed = parseEvent(trimmed, eventSchema);
          if (parsed !== null) queue.push(parsed);
        }
      }
      const next = queue.shift();
      if (next === undefined) return { done: true, value: undefined } as const;
      return { done: false, value: next } as const;
    },
    cancel: async () => {
      await reader.cancel();
    },
  };
}

function parseEvent<S extends z.ZodType>(
  raw: string,
  schema: S,
): z.output<S> | null {
  let json: unknown;
  try {
    json = JSON.parse(raw);
  } catch {
    return null;
  }
  const result = schema.safeParse(json);
  return result.success ? result.data : null;
}

export type {
  StreamPath,
  StreamInput,
  StreamEvent,
} from "./stream-endpoints.ts";
export { streamEndpoints } from "./stream-endpoints.ts";
